import React, { useEffect, useState } from "react";
import { Problem } from "../api/backendApi";
import { drawingApi } from "../api/drawingApi";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import { PlusCircleFill, Trash } from "react-bootstrap-icons";
import { useRoutablePages } from "../common/RoutablePages";
import { Button, Form, Modal } from "react-bootstrap";
import ConfirmModal from "../common/ConfirmModal";

function DrawingTemplatesPage() {
  const [templates, setTemplates] = useState<string[]>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Problem | string>();
  const [showAddNewTemplateModal, setShowAddNewTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState<string>();
  const [newTemplateBasedOn, setNewTemplateBasedOn] = useState<string>();
  const [newTemplateNameError, setNewTemplateNameError] = useState<
    string | Problem
  >();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>();
  const [templateBeingCreated, setTemplateBeingCreated] = useState(false);
  const navigate = useRoutablePages();

  useEffect(() => {
    async function loadTemplates() {
      setLoading(true);
      const result = await drawingApi.getDrawingTemplateList();
      setLoading(false);

      if (result.ok) {
        setError(undefined);
        setTemplates(result.value);
      } else {
        setError(result.problem);
      }
    }
    loadTemplates();
  }, []);

  const handleDelete = async () => {
    if (!selectedTemplate) return;

    setLoading(true);
    const result = await drawingApi.removeDrawingTemplate(selectedTemplate);
    setLoading(false);

    if (result.ok) {
      setError(undefined);

      if (templates) {
        const templateIndex = templates.indexOf(selectedTemplate);

        if (templateIndex >= 0) {
          setTemplates(
            templates?.filter((t, index) => index !== templateIndex),
          );
        }
      }
      setSelectedTemplate(undefined);
    } else {
      setError(result.problem);
    }
  };

  const handleCreate = async () => {
    if (!newTemplateName || newTemplateName.length === 0) {
      setNewTemplateNameError("A name is required.");
      return;
    }

    var result = await drawingApi.createDrawingTemplate(
      newTemplateName,
      newTemplateBasedOn,
    );

    if (result.ok) {
      setNewTemplateNameError(undefined);
      navigate.toDrawingTemplateEdit(newTemplateName);
      setShowAddNewTemplateModal(false);
    } else {
      setNewTemplateNameError(result.problem);
    }

    setTemplateBeingCreated(false);
  };

  return (
    <>
      <div className="main-container-title">
        <h1>
          Drawing Templates
          <PlusCircleFill
            className="text-primary"
            onClick={() => {
              setNewTemplateName("");
              setShowAddNewTemplateModal(true);
            }}
            width={32}
            height={32}
            style={{ marginLeft: "0.5rem" }}
            role="button"
          />
        </h1>

        <ErrorDisplay error={error} />
      </div>
      <div className="main-container-content">
        <div style={{ maxHeight: "100px" }}>
          <LoadingDisplay loading={loading} />
        </div>
        {templates && (
          <table
            className="table table-hover table-fluid"
            style={{ maxWidth: "800px" }}
          >
            <thead>
              <tr>
                <th>Name</th>
                <th style={{ width: "1px" }}></th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 && (
                <tr>
                  <td colSpan={2}>no templates</td>
                </tr>
              )}
              {templates.length > 0 &&
                templates.map((t, index) => (
                  <tr key={index}>
                    <td>
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate.toDrawingTemplateEdit(t)}
                      >
                        {t}
                      </span>
                    </td>
                    <td>
                      <Trash
                        role="button"
                        onClick={() => {
                          setSelectedTemplate(t);
                          setShowConfirmModal(true);
                        }}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
      <Modal
        show={showAddNewTemplateModal}
        onClose={() => setShowAddNewTemplateModal(false)}
      >
        <Modal.Header>
          <Modal.Title>Create new template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ErrorDisplay error={newTemplateNameError} />
          <Form.Group controlId="templateName">
            <Form.Label>Template name</Form.Label>
            <Form.Control
              type="text"
              defaultValue=""
              onChange={(e) => setNewTemplateName(e.currentTarget.value.trim())}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Based on</Form.Label>
            <Form.Select
              defaultValue={""}
              onChange={(e) => setNewTemplateBasedOn(e.currentTarget.value)}
            >
              <option value="">{"<default>"}</option>
              {templates &&
                templates.map((t) => (
                  <option key="t" value={t}>
                    {t}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowAddNewTemplateModal(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={templateBeingCreated}
            size="sm"
            onClick={() => {
              setTemplateBeingCreated(true);
              handleCreate();
            }}
          >
            Create
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmModal
        show={showConfirmModal}
        onOk={() => {
          setShowConfirmModal(false);
          handleDelete();
        }}
        onCancel={() => setShowConfirmModal(false)}
      />
    </>
  );
}

export default DrawingTemplatesPage;
